import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services/api'
import {
  User,
  UsersFilter,
  UsersResponse,
  CreateAdminRequest,
  CreatePlatformUserRequest,
  ReassignPlanRequest,
  DeleteUserRequest,
} from '@/types'
import { toast } from 'sonner'

const USERS_QUERY_KEY = 'users'

export const useUsers = (filters: UsersFilter = {}) => {
  return useQuery<UsersResponse>({
    queryKey: [USERS_QUERY_KEY, filters],
    queryFn: async () => {
      const params = new URLSearchParams()
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value))
        }
      })

      const { data } = await api.get(`/users?${params.toString()}`)
      return data
    },
  })
}

export const useUser = (id: string) => {
  return useQuery<User>({
    queryKey: [USERS_QUERY_KEY, id],
    queryFn: async () => {
      const { data } = await api.get(`/users/${id}`)
      return data
    },
    enabled: !!id,
  })
}

export const useUpdateUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<User> }) => {
      const { data } = await api.patch(`/users/${id}`, updates)
      return data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY, variables.id] })
      toast.success('Usuario actualizado exitosamente')
    },
  })
}

export const useSuspendUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, suspend }: { id: string; suspend: boolean }) => {
      const endpoint = suspend ? `/users/${id}/suspend` : `/users/${id}/unsuspend`
      const { data } = await api.post(endpoint)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] })
      toast.success('Estado del usuario actualizado')
    },
  })
}

export const useResetPassword = () => {
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post(`/users/${id}/reset-password`)
      return data
    },
    onSuccess: () => {
      toast.success('Contraseña restablecida. Se envió email al usuario.')
    },
  })
}

export const useUpdateUserQuota = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, maxDocs }: { id: string; maxDocs: number }) => {
      const { data } = await api.patch(`/users/${id}/quota`, { max_docs: maxDocs })
      return data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY, variables.id] })
      toast.success('Cuota actualizada exitosamente')
    },
  })
}

export const useUserActivity = (id: string) => {
  return useQuery({
    queryKey: [USERS_QUERY_KEY, id, 'activity'],
    queryFn: async () => {
      const { data } = await api.get(`/users/${id}/activity`)
      return data
    },
    enabled: !!id,
  })
}

export const useCreateAdmin = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: CreateAdminRequest) => {
      const { data } = await api.post('/users', payload)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] })
      toast.success('Admin creado exitosamente')
    },
  })
}

export const useCreatePlatformUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: CreatePlatformUserRequest) => {
      const { data } = await api.post('/users/platform', payload)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] })
      toast.success('Usuario de plataforma creado exitosamente')
    },
  })
}

export const useReassignPlan = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, request }: { id: string; request: ReassignPlanRequest }) => {
      const { data } = await api.post(`/users/${id}/reassign-plan`, request)
      return data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY, variables.id] })
      toast.success('Plan reasignado exitosamente')
    },
  })
}

export const useDeleteUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, request }: { id: string; request: DeleteUserRequest }) => {
      const { data } = await api.delete(`/users/${id}`, { data: request })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] })
      toast.success('Usuario eliminado permanentemente')
    },
  })
}
